import {
  COM_CASH_CURRENCIES,
  normalizeCashCurrency,
  RU_CASH_CURRENCIES,
  type CashCurrency,
} from '@lub0v/santa-utils';
import { isDotCom } from '@src/site';

const CURRENCY_SYMBOLS: Record<string, string> = {
  rub: '₽',
  kzt: '₸',
  usd: '$',
  eur: '€',
  uah: '₴',
  byn: 'Br',
  sek: 'kr',
  amd: '֏',
  dkk: 'kr',
  cad: 'C$',
  mxn: 'MX$',
  gbp: '£',
  chf: 'CHF',
  nok: 'kr',
  isk: 'kr',
  pln: 'zł',
  czk: 'Kč',
  huf: 'Ft',
  ron: 'lei',
  bgn: 'лв',
  rsd: 'din',
  brl: 'R$',
  ars: 'AR$',
  clp: 'CL$',
  cop: 'CO$',
  pen: 'S/',
  uyu: '$U',
  bob: 'Bs',
  pyg: '₲',
  aud: 'A$',
  nzd: 'NZ$',
  jpy: '¥',
  krw: '₩',
  sgd: 'S$',
};

export interface BudgetPresetAmounts {
  low: number;
  midMin: number;
  midMax: number;
}

const BUDGET_PRESET_AMOUNTS: Record<string, BudgetPresetAmounts> = {
  rub: { low: 1000, midMin: 1000, midMax: 3000 },
  usd: { low: 10, midMin: 10, midMax: 30 },
  eur: { low: 10, midMin: 10, midMax: 30 },
  uah: { low: 400, midMin: 400, midMax: 1200 },
  byn: { low: 30, midMin: 30, midMax: 90 },
  kzt: { low: 5000, midMin: 5000, midMax: 15000 },
  sek: { low: 100, midMin: 100, midMax: 300 },
  dkk: { low: 70, midMin: 70, midMax: 200 },
  amd: { low: 4000, midMin: 4000, midMax: 12000 },
  cad: { low: 15, midMin: 15, midMax: 40 },
  mxn: { low: 200, midMin: 200, midMax: 600 },
  gbp: { low: 10, midMin: 10, midMax: 25 },
  chf: { low: 15, midMin: 15, midMax: 40 },
  nok: { low: 100, midMin: 100, midMax: 300 },
  isk: { low: 1500, midMin: 1500, midMax: 4500 },
  pln: { low: 40, midMin: 40, midMax: 120 },
  czk: { low: 250, midMin: 250, midMax: 700 },
  huf: { low: 4000, midMin: 4000, midMax: 12000 },
  ron: { low: 50, midMin: 50, midMax: 150 },
  bgn: { low: 20, midMin: 20, midMax: 60 },
  rsd: { low: 1000, midMin: 1000, midMax: 3000 },
  brl: { low: 50, midMin: 50, midMax: 150 },
  ars: { low: 15000, midMin: 15000, midMax: 40000 },
  clp: { low: 10000, midMin: 10000, midMax: 30000 },
  cop: { low: 40000, midMin: 40000, midMax: 120000 },
  pen: { low: 40, midMin: 40, midMax: 120 },
  uyu: { low: 400, midMin: 400, midMax: 1200 },
  bob: { low: 70, midMin: 70, midMax: 200 },
  pyg: { low: 70000, midMin: 70000, midMax: 220000 },
  aud: { low: 15, midMin: 15, midMax: 45 },
  nzd: { low: 15, midMin: 15, midMax: 50 },
  jpy: { low: 1500, midMin: 1500, midMax: 4500 },
  krw: { low: 15000, midMin: 15000, midMax: 40000 },
  sgd: { low: 15, midMin: 15, midMax: 40 },
};

export function getCurrencySymbol(currency?: CashCurrency | string) {
  return CURRENCY_SYMBOLS[normalizeCashCurrency(currency)] || '';
}

export function getBudgetPresetAmounts(currency?: CashCurrency | string): BudgetPresetAmounts {
  const key = normalizeCashCurrency(currency);
  return BUDGET_PRESET_AMOUNTS[key] || BUDGET_PRESET_AMOUNTS.usd;
}

export function defaultCashCurrency(): CashCurrency {
  return isDotCom() ? 'usd' : 'rub';
}

export function cashCurrencyCodes(): CashCurrency[] {
  return [...(isDotCom() ? COM_CASH_CURRENCIES : RU_CASH_CURRENCIES)];
}

export function currencyOptionLabel(code: CashCurrency, title: string) {
  const symbol = getCurrencySymbol(code);
  return symbol ? `${title} (${symbol})` : title;
}

/** Localized budget text, with one currency name after the amount or range. */
export function formatBudgetAmount(
  minimum: number,
  maximum: number,
  currency: string,
  locale: string,
): string {
  const code = (currency || defaultCashCurrency()).toUpperCase();
  const numbers = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const range = minimum > 0 && minimum !== maximum;
  const amount = range
    ? `${numbers.format(minimum)}–${numbers.format(maximum)}`
    : numbers.format(maximum);
  const money = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    currencyDisplay: 'name',
  });
  let name =
    money.formatToParts(range ? 5 : maximum).find((part) => part.type === 'currency')?.value ||
    code;
  if (locale.startsWith('ru') && code === 'RUB') {
    const plural = range ? 'many' : new Intl.PluralRules(locale).select(maximum);
    name =
      plural === 'one'
        ? 'рубль'
        : plural === 'few'
          ? 'рубля'
          : plural === 'many'
            ? 'рублей'
            : 'рубля';
  }
  const symbol = getCurrencySymbol(currency || defaultCashCurrency());
  return `${symbol ? `${symbol} ` : ''}${amount} ${name}`;
}
